import { Anchor } from "@omni/ui/components/Anchor/anchor.component";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { InView } from "react-intersection-observer";
import { useSelector } from "react-redux";

import { ShoppingCartSummary } from "@/components/Cart/ShoppingCartSummary/shoppingCartSummary.component";
import { Divider } from "@/components/General/Divider/divider.component";
import { Spinner } from "@/components/General/Spinner/spinner.component";
import { useThunkDispatch } from "@/redux/dispatcher";
import { CartModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@/redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { TApiAction } from "@/redux/store.types";
import * as CartImpressionTracker from "@/services/tracking/trackers/impression/cartImpression.tracker";

export type MiniCartProps = {
    removeFromCart?: (id: string, quantity: number, free: boolean) => void;
    urlTarget?: string;
    shoppingCart?: CartModel | null;
    closeCallback?: () => void;
    isUpdatingCart?: { [key: string]: boolean };
    activeId: string;
};

const MiniCart = ({
    shoppingCart,
    urlTarget,
    closeCallback,
    removeFromCart,
    isUpdatingCart,
    activeId
}: MiniCartProps) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();
    const cartLoading = useSelector(CartSelectors.isLoadingCart);

    const onInView = (inView: boolean) => {
        if (!inView) return;

        if (shoppingCart) {
            CartImpressionTracker.trackCart(shoppingCart);
        } else {
            dispatch(CartModule.load()).then(
                (action: TApiAction<CartModel>) => {
                    if (
                        action.type === CartModule.constants.SUCCESS &&
                        action.payload
                    ) {
                        CartImpressionTracker.trackCart(action.payload);
                    }
                }
            );
        }
    };

    const hasCartItems = (shoppingCart?.items || []).length > 0;

    const cartKey = shoppingCart?.items.map((item) => item.id).join(",") ?? "";

    return (
        <InView
            className={cn(
                "tw:h-full tw:max-h-full! tw:flex tw:flex-col",
                "tw:lg:min-h-fit"
            )}
            triggerOnce={true}
            onChange={onInView}
        >
            <Divider className={cn("tw:mb-4")} />
            <section className={cn("tw:grow tw:overflow-hidden tw:-mr-4")}>
                {cartLoading && (
                    <div className={cn("tw:text-center tw:px-3")}>
                        <Spinner />
                    </div>
                )}
                {!cartLoading && (
                    <ShoppingCartSummary
                        key={cartKey}
                        productLinkingAvailable={true}
                        isMiniCart={true}
                        shoppingCart={shoppingCart}
                        removeFromCart={removeFromCart}
                        isUpdatingCart={isUpdatingCart}
                        activeId={activeId}
                        className={cn("tw:lg:max-h-[calc(100vh-200px)]")}
                    />
                )}
            </section>
            <section className={cn("tw:pt-4 tw:flex tw:gap-4")}>
                <Button
                    color={"link"}
                    onClick={closeCallback}
                    className={cn(
                        "tw:absolute tw:top-3 tw:left-4 tw:p-0 tw:w-full tw:text-left!",
                        "tw:lg:relative tw:lg:top-auto tw:lg:left-auto tw:lg:grow tw:lg:text-center"
                    )}
                >
                    <i className={cn("las la-angle-left")} />
                    {t("general.miniCart.button.continue")}
                </Button>
                {hasCartItems && (
                    <Anchor
                        color={"secondary"}
                        href={urlTarget}
                        fullWidth={true}
                        className={cn("tw:grow tw:w-full")}
                    >
                        {t("general.miniCart.button.order")}
                    </Anchor>
                )}
            </section>
        </InView>
    );
};

export { MiniCart };
